import { forwardRef } from 'react';
import type { ReactNode, Ref, PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import Text from '../Text';
import Flex from '../Flex';
import type {
  BaseProps,
  ForwardRefForwardPropsComponent,
  NoChildrenProp,
  TestIdProp
} from '../../types';
import { useTestIds } from '../../hooks';
import { withTestIds } from '../../utils';

import { getMetaListTestIds } from './MetaList.test-ids';

export interface MetaListProps extends BaseProps, NoChildrenProp, TestIdProp {
  /** Array of nodes or text to be rendered in the list. */
  items: ReactNode[];
  /**
   * If true, MetaList items that extend past the MetaList's container will wrap to a new line.
   * @default true
   */
  wrapItems?: boolean;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLUListElement>;
}

export const StyledMetaList = styled.ul<{ wrapItems: boolean }>(({ wrapItems }) => {
  return css`
    list-style: none;
    overflow: hidden;

    /* Negative margin to account for overflow clipping of focus indicator */
    padding: 0.3rem;
    margin: -0.3rem;

    ${!wrapItems &&
    css`
      white-space: nowrap;
    `}
  `;
});

export const StyledMetaListItem = styled.li<{ wrapItems: boolean }>(({ wrapItems }) => {
  return css`
    min-width: 0;
    display: inline-block;

    ${wrapItems
      ? css`
          overflow-wrap: break-word;
        `
      : css`
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        `}

    &[role='separator'] {
      -webkit-user-select: none;
      user-select: none;
    }
  `;
});

const MetaList: ForwardRefForwardPropsComponent<MetaListProps> = forwardRef(function MetaList(
  { testId, items, wrapItems = true, ...restProps }: PropsWithoutRef<MetaListProps>,
  ref: MetaListProps['ref']
) {
  const testIds = useTestIds(testId, getMetaListTestIds);
  const roleAttrValue = items.length <= 1 ? 'none' : undefined;

  return (
    <Flex
      data-testid={testIds.root}
      {...restProps}
      as={StyledMetaList}
      role={roleAttrValue}
      container={{ colGap: 0.5, alignItems: 'center', wrap: wrapItems ? 'wrap' : 'nowrap' }}
      wrapItems={wrapItems}
      ref={ref}
    >
      {items.flatMap((item, i, arr) =>
        item
          ? [
              <Text
                as={StyledMetaListItem}
                key={`${i + 0}`}
                variant='secondary'
                role={roleAttrValue}
                wrapItems={wrapItems}
              >
                {item}
              </Text>
            ].concat(
              i !== arr.length - 1
                ? [
                    <Text
                      as={StyledMetaListItem}
                      aria-hidden
                      key={`${i + 0}-sep`}
                      variant='secondary'
                    >
                      •
                    </Text>
                  ]
                : []
            )
          : []
      )}
    </Flex>
  );
});

export default withTestIds(MetaList, getMetaListTestIds);
